/**
 * Per-form generator dispatch.
 *
 * Moved from core-api a2a-integration/xmlForForm.ts (dispatchXml / dispatchCorrectionXml).
 * EXTRACTION NOTE: the correction path used to call getTransmitterData() here, a database
 * read. The transmitter now arrives on the context, so the dispatch is pure routing.
 */
import type { EngineContext, FormType } from "../context";

import { generate1099NECXml } from "../forms/1099-NEC/gen-xml";
import { generate1099MISCXml } from "../forms/1099-MISC/gen-xml";
import { generate1099INTXml } from "../forms/1099-INT/gen-xml";
import { generate1099DIVXml } from "../forms/1099-DIV/gen-xml";
import { generate1099RXml } from "../forms/1099-R/gen-xml";
import { generate1099KXml } from "../forms/1099-K/gen-xml";
import { generate1099SAXml } from "../forms/1099-SA/gen-xml";
import { generate1099CXml } from "../forms/1099-C/gen-xml";
import { generate1099LTCXml } from "../forms/1099-LTC/gen-xml";
import { generate1099BXml } from "../forms/1099-B/gen-xml";
import { generate1099QXml } from "../forms/1099-Q/gen-xml";
import { generate1098Xml } from "../forms/1098/gen-xml";
import { generate1098EXml } from "../forms/1098-E/gen-xml";
import { generate1098TXml } from "../forms/1098-T/gen-xml";
import { generate5498SAXml } from "../forms/5498-SA/gen-xml";
import { generate3921Xml } from "../forms/3921/gen-xml";
import { generate3922Xml } from "../forms/3922/gen-xml";
import { generate1042SXml } from "../forms/1042-S/gen-xml";

// Correction generators (TransmissionTypeCd='C' + CorrectedInd='1' + PrevSubmittedRecRecipientGrp).
import { generate1099NECCorrectionXml } from "../forms/1099-NEC/gen-correction-xml";
import { generate1099MISCCorrectionXml } from "../forms/1099-MISC/gen-correction-xml";
import { generate1099INTCorrectionXml } from "../forms/1099-INT/gen-correction-xml";
import { generate1099DIVCorrectionXml } from "../forms/1099-DIV/gen-correction-xml";
import { generate1099RCorrectionXml } from "../forms/1099-R/gen-correction-xml";
import { generate1099KCorrectionXml } from "../forms/1099-K/gen-correction-xml";
import { generate1099SACorrectionXml } from "../forms/1099-SA/gen-correction-xml";
import { generate1099CCorrectionXml } from "../forms/1099-C/gen-correction-xml";
import { generate1099LTCCorrectionXml } from "../forms/1099-LTC/gen-correction-xml";
import { generate1099BCorrectionXml } from "../forms/1099-B/gen-correction-xml";
import { generate1099QCorrectionXml } from "../forms/1099-Q/gen-correction-xml";
import { generate1098CorrectionXml } from "../forms/1098/gen-correction-xml";
import { generate1098ECorrectionXml } from "../forms/1098-E/gen-correction-xml";
import { generate1098TCorrectionXml } from "../forms/1098-T/gen-correction-xml";
import { generate5498SACorrectionXml } from "../forms/5498-SA/gen-correction-xml";
import { generate3921CorrectionXml } from "../forms/3921/gen-correction-xml";
import { generate3922CorrectionXml } from "../forms/3922/gen-correction-xml";
import { generate1042SCorrectionXml } from "../forms/1042-S/gen-correction-xml";

export interface GeneratedXml {
  xml: string;
  taxYear?: string;
}

/** Original / replacement transmissions: the standard per-form generator. */
export async function dispatchXml(
  ctx: EngineContext,
  formType: FormType | string,
  records: any[]
): Promise<GeneratedXml> {
  switch (formType) {
    case "1099-NEC":  return generate1099NECXml(ctx, records as any);
    case "1099-MISC": return generate1099MISCXml(ctx, records as any);
    case "1099-INT":  return generate1099INTXml(ctx, records as any);
    case "1099-DIV":  return generate1099DIVXml(ctx, records as any);
    case "1099-R":    return generate1099RXml(ctx, records as any);
    case "1099-K":    return generate1099KXml(ctx, records as any);
    case "1099-SA":   return generate1099SAXml(ctx, records as any);
    case "1099-C":    return generate1099CXml(ctx, records as any);
    case "1099-LTC":  return generate1099LTCXml(ctx, records as any);
    case "1099-B":    return generate1099BXml(ctx, records as any);
    case "1099-Q":    return generate1099QXml(ctx, records as any);
    case "1098":      return generate1098Xml(ctx, records as any);
    case "1098-E":    return generate1098EXml(ctx, records as any);
    case "1098-T":    return generate1098TXml(ctx, records as any);
    case "5498-SA":   return generate5498SAXml(ctx, records as any);
    case "3921":      return generate3921Xml(ctx, records as any);
    case "3922":      return generate3922Xml(ctx, records as any);
    case "1042-S":
    case "1042S":     return generate1042SXml(ctx, records as any);
    default: throw new Error(`Unsupported formType: ${formType}`);
  }
}

/** Dispatch to the per-form Correction generator (TransmissionTypeCd='C', CorrectedInd='1'). */
export async function dispatchCorrectionXml(
  ctx: EngineContext,
  formType: FormType | string,
  records: any[]
): Promise<GeneratedXml> {
  const t = ctx.transmitter as any;
  switch (formType) {
    case "1099-NEC":  return generate1099NECCorrectionXml(ctx, records as any, t);
    case "1099-MISC": return generate1099MISCCorrectionXml(ctx, records as any, t);
    case "1099-INT":  return generate1099INTCorrectionXml(ctx, records as any, t);
    case "1099-DIV":  return generate1099DIVCorrectionXml(ctx, records as any, t);
    case "1099-R":    return generate1099RCorrectionXml(ctx, records as any, t);
    case "1099-K":    return generate1099KCorrectionXml(ctx, records as any, t);
    case "1099-SA":   return generate1099SACorrectionXml(ctx, records as any, t);
    case "1099-C":    return generate1099CCorrectionXml(ctx, records as any, t);
    case "1099-LTC":  return generate1099LTCCorrectionXml(ctx, records as any, t);
    case "1099-B":    return generate1099BCorrectionXml(ctx, records as any, t);
    case "1099-Q":    return generate1099QCorrectionXml(ctx, records as any, t);
    case "1098":      return generate1098CorrectionXml(ctx, records as any, t);
    case "1098-E":    return generate1098ECorrectionXml(ctx, records as any, t);
    case "1098-T":    return generate1098TCorrectionXml(ctx, records as any, t);
    case "5498-SA":   return generate5498SACorrectionXml(ctx, records as any, t);
    case "3921":      return generate3921CorrectionXml(ctx, records as any, t);
    case "3922":      return generate3922CorrectionXml(ctx, records as any, t);
    case "1042-S":
    case "1042S":     return generate1042SCorrectionXml(ctx, records as any, t);
    default: throw new Error(`Unsupported correction formType: ${formType}`);
  }
}
